import React, { useMemo, useRef, useLayoutEffect, useState } from 'react';
import { mapToStandardColor } from 'constants/colors';

interface Tag {
  id: string;
  name: string;
  color: string;
}

const GAP = 8;
const MIN_TRUNCATED_DISPLAY = 30;
const BADGE_WIDTH_SM = 28;
const BADGE_WIDTH_MD = 36;
const BADGE_WIDTH_LG = 44;
const BADGE_THRESHOLD_MD = 10;
const BADGE_THRESHOLD_LG = 100;
const GAPS_NEEDED = 2;

function getBadgeWidth(hiddenCount: number): number {
  if (hiddenCount >= BADGE_THRESHOLD_LG) return BADGE_WIDTH_LG;
  if (hiddenCount >= BADGE_THRESHOLD_MD) return BADGE_WIDTH_MD;
  if (hiddenCount > 0) return BADGE_WIDTH_SM;
  return 0;
}

function fitsWithBadge(
  newUsed: number,
  remaining: number,
  containerWidth: number
): boolean {
  const estimatedBadgeWidth = getBadgeWidth(remaining);
  // gaps: one after the current tag, one between the next tag and the overflow badge
  return newUsed + GAPS_NEEDED * GAP + estimatedBadgeWidth <= containerWidth;
}

function adjustForTruncation(
  count: number,
  tagEls: HTMLElement[],
  usedWidth: number,
  containerWidth: number
): number {
  if (count <= 0 || count >= tagEls.length) return count;

  const hiddenCountAfter = tagEls.length - count - 1;
  const estimatedBadgeWidth = getBadgeWidth(hiddenCountAfter);
  const badgeSlot = estimatedBadgeWidth > 0 ? GAP + estimatedBadgeWidth : 0;
  const truncatedAvailable = containerWidth - usedWidth - GAP - badgeSlot;

  return truncatedAvailable < MIN_TRUNCATED_DISPLAY
    ? Math.max(0, count - 1)
    : count;
}

function computeVisibleCount(
  tagEls: HTMLElement[],
  containerWidth: number
): number {
  let usedWidth = 0;
  let count = 0;

  for (let i = 0; i < tagEls.length; i += 1) {
    const tagWidth = tagEls[i].offsetWidth;
    const isLast = i === tagEls.length - 1;
    const remaining = tagEls.length - i - 1;
    const newUsed = usedWidth + (i > 0 ? GAP : 0) + tagWidth;

    if (isLast) {
      if (newUsed <= containerWidth) count = tagEls.length;
      break;
    }

    const fits =
      remaining === 1
        ? newUsed + GAP <= containerWidth
        : fitsWithBadge(newUsed, remaining, containerWidth);

    if (fits) {
      usedWidth = newUsed;
      count = i + 1;
    } else {
      break;
    }
  }

  return adjustForTruncation(count, tagEls, usedWidth, containerWidth);
}

interface TagChipProps {
  tag: Tag;
  truncate?: boolean;
}

const TagChip: React.FC<TagChipProps> = ({ tag, truncate }) => {
  const colorMapping = mapToStandardColor(tag.color);

  return (
    <div
      style={{
        backgroundColor: colorMapping.background,
        color: colorMapping.text,
        borderRadius: '4px',
        padding: '0 8px',
        height: '20px',
        lineHeight: '20px',
        fontSize: '12px',
        fontWeight: 600,
        flexShrink: truncate ? 1 : 0,
        minWidth: truncate ? 0 : undefined,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
      title={tag.name}
    >
      {tag.name}
    </div>
  );
};

interface OverflowBadgeProps {
  count: number;
}

const OverflowBadge: React.FC<OverflowBadgeProps> = ({ count }) => {
  return (
    <div
      style={{
        backgroundColor: '#DFE2E7',
        color: '#4D5165',
        borderRadius: '4px',
        padding: '0 4px',
        height: '20px',
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        fontSize: '12px',
        fontWeight: 600,
        flexShrink: 0,
        minWidth: 'fit-content'
      }}
    >
      +{count}
    </div>
  );
};

interface TagsRowProps {
  tagIds?: string[];
  allTags: Tag[];
}

const TagsRow: React.FC<TagsRowProps> = ({ tagIds, allTags }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  const resolvedTags = useMemo(() => {
    if (!tagIds || !allTags) return [];
    return tagIds
      .map(id => allTags.find(t => t.id === id))
      .filter((tag): tag is Tag => Boolean(tag));
  }, [tagIds, allTags]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || resolvedTags.length === 0) {
      setVisibleCount(resolvedTags.length);
      return;
    }

    const containerWidth = container.offsetWidth;
    if (containerWidth === 0) return;

    const measureDiv = container.querySelector(
      '[data-measure-tags]'
    ) as HTMLDivElement;
    if (!measureDiv) return;

    const tagEls = Array.from(measureDiv.children) as HTMLElement[];
    setVisibleCount(computeVisibleCount(tagEls, containerWidth));
  }, [resolvedTags]);

  if (resolvedTags.length === 0) {
    return null;
  }

  const hiddenCount =
    visibleCount === null ? 0 : resolvedTags.length - visibleCount - 1;
  const displayTags =
    visibleCount === null ? [] : resolvedTags.slice(0, visibleCount + 1);

  return (
    <div className="w-full overflow-hidden" ref={containerRef}>
      {/* Measurement phase: all tags rendered invisibly */}
      {visibleCount === null && (
        <div
          data-measure-tags
          style={{
            display: 'flex',
            gap: '8px',
            visibility: 'hidden',
            position: 'absolute',
            pointerEvents: 'none'
          }}
        >
          {resolvedTags.map(tag => (
            <TagChip key={tag.id} tag={tag} />
          ))}
        </div>
      )}

      {/* Display phase: only visible tags + badge */}
      {visibleCount !== null && (
        <div style={{ display: 'flex', gap: '8px', overflow: 'hidden' }}>
          {displayTags.map((tag, idx) => {
            const isLastVisible = idx === displayTags.length - 1;
            const hasExtraTags = resolvedTags.length > visibleCount;
            return (
              <TagChip
                key={tag.id}
                tag={tag}
                truncate={isLastVisible && hasExtraTags}
              />
            );
          })}
          {hiddenCount > 0 && <OverflowBadge count={hiddenCount} />}
        </div>
      )}
    </div>
  );
};

export default TagsRow;
